import React, { useMemo } from 'react';

import {
  CaretDownFilled,
  CaretRightFilled,
  CheckOutlined,
  EnterOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import { LocationStatusValues } from '@recurrency/core-api-schema/dist/itemLocations/getItemLocations';
import { theme } from 'theme';
import { fontWeights } from 'theme/typography';

import { TooltipStatusBadge } from 'pages/purchasing/common/TooltipStatusBadge';
import { useAggregatedNetstockEnabled } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/utils';
import { ItemReplenishmentPathNode } from 'pages/purchasing/PurchaseTargetsPage/types';

import { Alert } from 'components/Alert';
import { NetStockPopover, NetStockRecord } from 'components/recipes/equation/NetStockPopover';
import { QtyToOrderPopover, QtyToOrderRecord } from 'components/recipes/equation/QtyToOrderPopover';
import { Table } from 'components/Table';
import { IconTooltip } from 'components/Tooltip/IconTooltip';

import { formatNumber } from 'utils/formatting';
import { PersistedColumn } from 'utils/tableAndSidePaneSettings/types';
import { asKeyOf, numberColumn } from 'utils/tables';

import { mapAggregatedToNetStockRecord, ViewReplenishmentPathsTreeProps } from './utils';

export function ViewReplenishmentPathsTable({
  replenishmentPathTree,
  requirementLocationId,
  defaultExpandAll,
  itemStatusByLocation,
}: Omit<ViewReplenishmentPathsTreeProps, 'requirementLocationId'> & {
  requirementLocationId: string;
  itemStatusByLocation: Record<string, LocationStatusValues>;
}) {
  const isSingleLocation =
    replenishmentPathTree.length === 1 &&
    (!replenishmentPathTree[0].children || replenishmentPathTree[0].children.length === 0);

  const purchaseLocation = replenishmentPathTree[0].locationId;
  const { enabled: aggregatedNetstockEnabled } = useAggregatedNetstockEnabled();

  const tableColumns = useMemo(
    () =>
      replenishmentPathColumns({
        requirementLocation: requirementLocationId,
        purchaseLocation,
        itemStatusByLocation,
        useAggregatedNetStock: aggregatedNetstockEnabled,
      }),
    [itemStatusByLocation, purchaseLocation, requirementLocationId, aggregatedNetstockEnabled],
  );

  return (
    <>
      {isSingleLocation && (
        <Alert
          message="This location purchases for itself and doesn't replenish any other locations."
          icon={<InfoCircleOutlined />}
          type="info"
          banner
          className={css`
            margin-bottom: 16px;
          `}
        />
      )}
      <Table
        style={{ tableLayout: 'auto' }}
        className={css`
          .ant-table-selection-col,
          .ant-table-selection-column {
            display: none;
          }

          .ant-table-cell,
          .location-col {
            white-space: nowrap;
            margin-left: 4px;
          }
        `}
        // Select the requirement location by default, disable modifying the selection, and hide column
        rowSelection={{
          selectedRowKeys: [requirementLocationId],
          renderCell: () => null,
          hideSelectAll: true,
          columnWidth: '14px',
        }}
        data={replenishmentPathTree}
        columns={tableColumns}
        pagination={false}
        expandable={{
          defaultExpandAllRows: defaultExpandAll,
          indentSize: 20,
          // Changes icon direction based on expand/collapse state
          expandIcon: ({ onExpand, record, expanded }) => {
            const expandable = record.children?.length !== undefined && record.children?.length > 0;
            if (!expandable) {
              return (
                <EnterOutlined
                  style={{
                    transform: 'scaleX(-1.3) scaleY(1.3)',
                    color: '#d9d9d9',
                    margin: ' -2px 8px',
                    height: '1.5em',
                  }}
                />
              );
            }
            return (
              <span
                style={{ margin: '0 6px 0 8px', cursor: 'pointer' }}
                onClick={(e) => {
                  onExpand(record, e);
                }}
              >
                {expanded ? <CaretDownFilled /> : <CaretRightFilled />}
              </span>
            );
          },
        }}
        rowKey="key"
      />
    </>
  );
}

const replenishmentPathColumns = ({
  requirementLocation,
  purchaseLocation,
  itemStatusByLocation,
  useAggregatedNetStock,
}: {
  requirementLocation: string;
  purchaseLocation: string;
  itemStatusByLocation: Record<string, LocationStatusValues>;
  useAggregatedNetStock: boolean;
}): PersistedColumn<ItemReplenishmentPathNode>[] => [
  {
    key: 'locationId',
    title: () => (
      <span
        className={css`
          margin-left: 10px;
        `}
      >
        Location
      </span>
    ),
    dataIndex: asKeyOf<ItemReplenishmentPathNode>('locationId'),
    settingKey: 'locationId',
    align: 'left',
    className: 'location-col',
    render: (locationId: string) => {
      const isRequirementLocation = requirementLocation === locationId;
      const isPurchaseLocation = purchaseLocation === locationId;
      return (
        <span
          className={css`
            font-weight: ${isRequirementLocation ? fontWeights.bold : 'inherit'};
            position: relative;
            top: ${!isPurchaseLocation ? '2px' : '0'};
          `}
        >
          {locationId}: {itemStatusByLocation[locationId].locationName}
        </span>
      );
    },
  },
  {
    key: 'stockable',
    title: 'Stockable',
    dataIndex: 'locationId',
    settingKey: 'stockable',
    align: 'center',
    render: (locationId: string) => (itemStatusByLocation[locationId].stockable ? <CheckOutlined /> : null),
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'locationId',
    settingKey: 'status',
    align: 'center',
    render: (locationId: string) => (
      <TooltipStatusBadge
        record={{
          ...itemStatusByLocation[locationId],
          netStock: itemStatusByLocation[locationId].aggregatedQuantityFields.aggregatedNetStock,
          status: itemStatusByLocation[locationId].status,
        }}
      />
    ),
  },
  ...(useAggregatedNetStock
    ? aggregatedNetStockColumns(itemStatusByLocation)
    : nonAggregatedNetStockColumns(itemStatusByLocation)),
  numberColumn({
    key: 'qtyRequired',
    title: 'Required',
    dataIndex: 'locationId',
    settingKey: 'qtyRequired',
    align: 'left',
    render: (_: number, record: ItemReplenishmentPathNode) => (
      <>
        {formatNumber(itemStatusByLocation[record.locationId].qtyToOrder)}{' '}
        {itemStatusByLocation[record.locationId].unitOfMeasure}
        <QtyToOrderPopover
          disableWarning
          record={
            {
              ...(useAggregatedNetStock
                ? mapAggregatedToNetStockRecord(itemStatusByLocation[record.locationId].aggregatedQuantityFields)
                : itemStatusByLocation[record.locationId].aggregatedQuantityFields),
              ...itemStatusByLocation[record.locationId],
            } as QtyToOrderRecord
          }
          isAggregatedNetStock={useAggregatedNetStock}
          title="Quantity Required Calculation"
        />
      </>
    ),
  }),
];

const aggregatedNetStockColumns = (
  itemStatusByLocation: Record<string, LocationStatusValues>,
): PersistedColumn<ItemReplenishmentPathNode>[] => [
  numberColumn({
    key: 'aggregatedNetStock',
    title: () => (
      <>
        Net Stock
        <IconTooltip
          tooltipText="Sum of net stock at this location and all downstream spokes it replenishes"
          wrapText
          Icon={QuestionCircleOutlined}
          iconStyle={{ margin: '4px', color: theme.colors.neutral[900] }}
        />
      </>
    ),
    dataIndex: ['aggregatedQuantityFields', 'aggregatedNetStock'],
    settingKey: 'aggregatedQuantityFields.aggregatedNetStock',
    align: 'left',
    render: (_: number, record: ItemReplenishmentPathNode) => (
      <>
        {formatNumber(itemStatusByLocation[record.locationId].aggregatedQuantityFields.aggregatedNetStock)}{' '}
        {itemStatusByLocation[record.locationId].unitOfMeasure}
        <NetStockPopover
          key={`aggregated_ns_pop_${record.locationId}`}
          title="Aggregated Net Stock Across Downstream Spokes"
          record={
            {
              ...mapAggregatedToNetStockRecord(itemStatusByLocation[record.locationId].aggregatedQuantityFields),
              ...itemStatusByLocation[record.locationId],
            } as NetStockRecord
          }
          isAggregatedNetStock
        />
      </>
    ),
  }),

  numberColumn({
    key: 'netStock',
    title: 'Location Stock',
    dataIndex: ['quantityFields', 'netStock'],
    settingKey: 'quantityFields.netStock',
    render: (_: number, record: ItemReplenishmentPathNode) => (
      <>
        {formatNumber(itemStatusByLocation[record.locationId].quantityFields.netStock)}{' '}
        {itemStatusByLocation[record.locationId].unitOfMeasure}
        <NetStockPopover
          key={`location_ns_pop_${record.locationId}`}
          record={
            {
              ...itemStatusByLocation[record.locationId].quantityFields,
              aggregatedNetStock: itemStatusByLocation[record.locationId].aggregatedQuantityFields.aggregatedNetStock,
              unitOfMeasure: itemStatusByLocation[record.locationId].unitOfMeasure,
            } as NetStockRecord
          }
        />
      </>
    ),
    align: 'left',
  }),
];
const nonAggregatedNetStockColumns = (
  itemStatusByLocation: Record<string, LocationStatusValues>,
): PersistedColumn<ItemReplenishmentPathNode>[] => [
  numberColumn({
    key: 'netStock',
    title: 'Net Stock',
    dataIndex: ['quantityFields', 'netStock'],
    settingKey: 'quantityFields.netStock',
    render: (_: number, record: ItemReplenishmentPathNode) => (
      <>
        {formatNumber(itemStatusByLocation[record.locationId].quantityFields.netStock)}{' '}
        {itemStatusByLocation[record.locationId].unitOfMeasure}
        <NetStockPopover
          key={`location_ns_pop_${record.locationId}`}
          record={
            {
              ...itemStatusByLocation[record.locationId].quantityFields,
              unitOfMeasure: itemStatusByLocation[record.locationId].unitOfMeasure,
              aggregatedNetStock: itemStatusByLocation[record.locationId].quantityFields.netStock,
            } as NetStockRecord
          }
        />
      </>
    ),
    align: 'left',
  }),
];
